import React from 'react';
import { Sparkles, Bot, Cpu, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';

/**
 * AI Content Badge - Displays when content is AI-generated or AI-assisted
 * Part of Socyy's transparency and compliance system
 */

export const AIBadge = ({ 
  type = 'generated', // 'generated', 'assisted', 'suggested', 'analyzed'
  model = null,
  showTooltip = true,
  size = 'sm', // 'xs', 'sm', 'md'
  className = ''
}) => {
  const config = {
    generated: {
      icon: Sparkles,
      label: 'AI Generated',
      color: 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white',
      tooltip: 'This content was fully generated by AI'
    },
    assisted: {
      icon: Bot,
      label: 'AI Assisted',
      color: 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white',
      tooltip: 'This content was created with AI assistance'
    },
    suggested: {
      icon: Cpu,
      label: 'AI Suggested',
      color: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white',
      tooltip: 'This content was suggested by AI'
    },
    analyzed: {
      icon: Info,
      label: 'AI Analyzed',
      color: 'bg-gray-600 text-white',
      tooltip: 'This content was analyzed by AI'
    }
  };

  const { icon: Icon, label, color, tooltip } = config[type] || config.generated;
  
  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1'
  };

  const iconSizes = {
    xs: 'w-2.5 h-2.5',
    sm: 'w-3 h-3',
    md: 'w-4 h-4'
  };

  const badge = (
    <Badge 
      className={`${color} ${sizeClasses[size]} font-medium inline-flex items-center gap-1 ${className}`}
      data-testid="ai-content-badge"
    >
      <Icon className={iconSizes[size]} />
      <span>{label}</span>
      {model && size !== 'xs' && (
        <span className="opacity-75 text-[10px]">({model})</span>
      )}
    </Badge>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-sm">{tooltip}</p>
          {model && <p className="text-xs text-gray-400 mt-1">Model: {model}</p>}
          <p className="text-xs text-gray-400 mt-1">
            Socyy clearly labels AI content for transparency.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * AI Disclosure Banner - Full disclosure for AI-generated posts
 */
export const AIDisclosureBanner = ({ 
  model = null, 
  generatedAt = null,
  contentType = 'content'
}) => {
  return (
    <div 
      className="bg-gradient-to-r from-violet-50 to-fuchsia-50 dark:from-violet-900/20 dark:to-fuchsia-900/20 
                 border border-violet-200 dark:border-violet-800 rounded-lg p-3 mb-3"
      data-testid="ai-disclosure-banner"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-violet-700 dark:text-violet-300">
            AI-Generated {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
          </p>
          <p className="text-xs text-violet-600 dark:text-violet-400 mt-0.5">
            This {contentType} was created using artificial intelligence.
            {model && ` Model: ${model}`}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Inline AI indicator for feeds
 */
export const AIIndicator = ({ isAI = false, aiAssisted = false, className = '' }) => {
  if (!isAI && !aiAssisted) return null;

  return (
    <span 
      className={`inline-flex items-center gap-0.5 text-xs text-violet-500 ${className}`}
      data-testid="ai-indicator"
    >
      <Sparkles className="w-3 h-3" />
      <span>{isAI ? 'AI' : 'AI-Assisted'}</span>
    </span>
  );
};

export default AIBadge;
